<template>
  <v-row justify="center">
    <v-col cols="12" md="8">

      <div class="d-flex align-center justify-space-between mb-4">
        <div class="text-h6">Dueños de la empresa</div>
        <v-btn color="primary" small @click="mostrarFormulario = !mostrarFormulario">
          <v-icon left small>mdi-plus</v-icon>
          Registrar dueño
        </v-btn>
      </div>

      <!-- Formulario nuevo dueño -->
      <v-card v-if="mostrarFormulario" outlined class="mb-4">
        <v-card-title class="text-subtitle-2">Nuevo dueño</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-text-field outlined dense v-model="form.nombre" label="Nombre *"
                v-validate="'required|max:25'" :error-messages="errors.collect('nombre')"
                data-vv-name="nombre" />
            </v-col>
            <v-col cols="6">
              <v-text-field outlined dense v-model="form.apellido" label="Apellido *"
                v-validate="'required|max:25'" :error-messages="errors.collect('apellido')"
                data-vv-name="apellido" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <TipoDocumentoVSelect :entity="form" :error="errors.collect('tipoDocumento')" />
            </v-col>
            <v-col cols="8">
              <v-text-field outlined dense v-model="form.documento" label="Documento *"
                v-validate="'required|numeric|min:7|max:11'" :error-messages="errors.collect('documento')"
                data-vv-name="documento" />
            </v-col>
          </v-row>
          <v-text-field outlined dense v-model="form.email" label="Email"
            v-validate="'email'" :error-messages="errors.collect('email')"
            data-vv-name="email" hint="Opcional" persistent-hint />

          <v-divider class="my-3"></v-divider>
          <div class="text-caption grey--text mb-2">Acceso al sistema (opcional)</div>
          <v-row>
            <v-col cols="6">
              <v-text-field outlined dense v-model="form.username" label="Usuario"
                hint="Si no se carga no se crea login" persistent-hint />
            </v-col>
            <v-col cols="6">
              <v-text-field outlined dense v-model="form.password" label="Contraseña"
                type="password" :disabled="!form.username"
                :hint="form.username ? 'Requerida si se carga usuario' : ''" persistent-hint />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="cancelar">Cancelar</v-btn>
          <v-btn color="primary" small :loading="isLoading" @click="registrar">Registrar</v-btn>
        </v-card-actions>
      </v-card>

      <!-- Lista de dueños con expansión de sucursales -->
      <v-card outlined>
        <v-expansion-panels flat accordion>
          <v-expansion-panel v-for="dueño in dueños" :key="dueño.id"
            @click="cargarSucursalesDueño(dueño)">
            <v-expansion-panel-header>
              <div class="d-flex align-center">
                <v-avatar size="32" color="grey lighten-3" class="mr-3">
                  <v-icon small>mdi-account</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ dueño.persona.nombre }} {{ dueño.persona.apellido }}</div>
                  <div class="text-caption grey--text">{{ dueño.persona.email || 'Sin email' }}</div>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-divider class="mb-3"></v-divider>
              <div class="text-caption grey--text mb-2 text-uppercase">Sucursales</div>

              <div v-if="loadingSucursales[dueño.id]" class="text-center pa-2">
                <v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
              </div>

              <v-list v-else dense>
                <v-list-item v-for="sucursal in sucursales" :key="sucursal.id">
                  <v-list-item-icon>
                    <v-icon small :color="estaAsignado(dueño.id, sucursal.uuid) ? 'primary' : 'grey lighten-1'">
                      mdi-store
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ sucursal.nombre }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      x-small
                      :color="estaAsignado(dueño.id, sucursal.uuid) ? 'error' : 'primary'"
                      outlined
                      :loading="loadingAsignacion[`${dueño.id}-${sucursal.uuid}`]"
                      @click.stop="toggleAsignacion(dueño, sucursal)"
                    >
                      {{ estaAsignado(dueño.id, sucursal.uuid) ? 'Quitar' : 'Asignar' }}
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>

            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-list v-if="dueños.length === 0">
          <v-list-item>
            <v-list-item-content class="grey--text">No hay dueños registrados.</v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>

    </v-col>
  </v-row>
</template>

<script>
import DueñoAPI from "@/api/dueños";
import TipoDocumentoVSelect from "@/components/util/TipoDocumentoVSelect.vue";

export default {
  name: "EmpresaDueñosHome",
  components: { TipoDocumentoVSelect },
  $_veeValidate: { validator: "new" },
  props: {
    empresa: { type: Object, default: null },
  },
  data() {
    return {
      dueños: [],
      sucursales: [],
      // { [dueño_id]: [uuid_sucursal, ...] }
      asignaciones: {},
      loadingSucursales: {},
      loadingAsignacion: {},
      mostrarFormulario: false,
      isLoading: false,
      form: this.formVacio(),
    };
  },
  computed: {
    uuid_empresa() {
      return this.$route.params.uuid_empresa;
    },
  },
  methods: {
    formVacio() {
      return { nombre: "", apellido: "", documento: "", tipo_documento: null, email: "", username: "", password: "" };
    },
    cancelar() {
      this.mostrarFormulario = false;
      this.form = this.formVacio();
      this.$validator.reset();
    },
    async fetchDueños() {
      try {
        const response = await this.axios.get(`/empleados/dueño?uuid_empresa=${this.uuid_empresa}`);
        this.dueños = response.data;
      } catch (error) {
        console.error(error);
      }
    },
    async fetchSucursales() {
      try {
        const response = await this.axios.get(`/sucursales?uuid=${this.uuid_empresa}`);
        this.sucursales = Array.isArray(response.data) ? response.data : [response.data];
      } catch (error) {
        console.error(error);
      }
    },
    async cargarSucursalesDueño(dueño) {
      // Si ya cargamos las asignaciones de este dueño, no volver a cargar
      if (this.asignaciones[dueño.id] !== undefined) return;

      this.$set(this.loadingSucursales, dueño.id, true);
      try {
        // Consultamos cada sucursal para ver si el dueño está asignado
        const uuidsAsignados = [];
        await Promise.all(
          this.sucursales.map(async (sucursal) => {
            const res = await this.axios.get(`/empleados/asignaciones/owner/${sucursal.uuid}`);
            const asignados = res.data.dueños_sucursal || [];
            if (asignados.some(d => d.id === dueño.id)) {
              uuidsAsignados.push(sucursal.uuid);
            }
          })
        );
        this.$set(this.asignaciones, dueño.id, uuidsAsignados);
      } catch (error) {
        console.error(error);
      } finally {
        this.$set(this.loadingSucursales, dueño.id, false);
      }
    },
    estaAsignado(dueñoId, uuidSucursal) {
      return (this.asignaciones[dueñoId] || []).includes(uuidSucursal);
    },
    async toggleAsignacion(dueño, sucursal) {
      const key = `${dueño.id}-${sucursal.uuid}`;
      const accion = this.estaAsignado(dueño.id, sucursal.uuid) ? 'quitar' : 'asignar';
      this.$set(this.loadingAsignacion, key, true);
      try {
        const res = await this.axios.post('/empleados/asignaciones/gestion/owner', {
          dueño_id: dueño.id,
          accion,
          sucursal_uuid: sucursal.uuid,
        });
        // Actualizar asignaciones localmente
        const actuales = [...(this.asignaciones[dueño.id] || [])];
        if (accion === 'asignar') {
          actuales.push(sucursal.uuid);
        } else {
          const idx = actuales.indexOf(sucursal.uuid);
          if (idx > -1) actuales.splice(idx, 1);
        }
        this.$set(this.asignaciones, dueño.id, actuales);
        this.$updateBus.$emit("showSnackBar", {
          show: true, button: false, color: "success",
          text: res.data.detail, icon: "mdi-check", timeout: 2500,
        });
      } catch (error) {
        this.$updateBus.$emit("showSnackBar", {
          show: true, button: false, color: "error",
          text: error.response ? error.response.data.detail : "Error inesperado",
          icon: "mdi-alert", timeout: 3000,
        });
      } finally {
        this.$set(this.loadingAsignacion, key, false);
      }
    },
    async registrar() {
      const valid = await this.$validator.validateAll();
      if (!valid) return;
      try {
        this.isLoading = true;
        const payload = {
          uuid_empresa: this.uuid_empresa,
          nombre: this.form.nombre,
          apellido: this.form.apellido,
          documento: this.form.documento,
          tipo_documento: this.form.tipo_documento,
        };
        if (this.form.email) payload.email = this.form.email;
        if (this.form.username) {
          payload.username = this.form.username;
          payload.password = this.form.password;
        }
        await DueñoAPI.create(payload);
        this.$updateBus.$emit("showSnackBar", {
          show: true, button: false, color: "success",
          text: "Dueño registrado", icon: "mdi-check", timeout: 3000,
        });
        this.cancelar();
        this.fetchDueños();
      } catch (error) {
        const msg = error.response ? JSON.stringify(error.response.data) : "Error inesperado";
        this.$updateBus.$emit("showSnackBar", {
          show: true, button: false, color: "error",
          text: msg, icon: "mdi-alert", timeout: 4000,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.fetchDueños();
    this.fetchSucursales();
  },
};
</script>
